import {
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Grid,
  Group,
  Input,
  rem,
  Select,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import useStyles from "./style";
import BlueInfoSvg from "../Svgs/BlueInfoSvg";
import { RxCross1 } from "react-icons/rx";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import useDebounce from "../../utils/useDebounce";
import { IconSearch } from "@tabler/icons-react";
import { getStaffByIdSkypoint } from "../../services/staff";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import SkypointActionButtons from "./SkypointActionButtons";
import { getAllSkypointProvider } from "../../services/skypointProvider";

/**
 * StaffSettings component
 *
 * This component manages the settings related to staff and their assigned
 * facilities and clients. It allows selecting and managing facilities and
 * clients associated with a staff member. The component supports adding,
 * editing, and viewing modes of operation.
 *
 * @param {object} data - The staff data object containing staff details.
 * @param {string} mode - The mode of the component, can be "add", "edit", or "view".
 * @param {function} handleCancel - A function to handle the cancel action.
 * @param {object} staffResponse - The response object containing staff details after an action.
 * @param {string} activeTab - The currently active tab in the UI.
 */

export const SkypointStaffSettings = ({
  data,
  mode,
  handleCancel,
  staffResponse,
  activeTab,
  handleSave,
  form,
  addedProviders,
  setAddedProviders,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { skypointProviders } = useSelector(
    (state) => state?.skypointProviders
  );
  const { addStaffLoading } = useSelector((state) => state?.staff);
  const selectRef = useRef(null);

  const icon = (
    <DropDownArrowSvg
      color={true}
      style={{ width: rem(0.5), height: rem(0.5) }}
    />
  );

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      getStaffByIdSkypoint(staffResponse?.id || data?.id);
    }
  }, []);

  useEffect(() => {
    getAllSkypointProvider("all");
  }, []);

  const ProviderItem = forwardRef(
    ({ label, value, onAddClick, added }, ref) => (
      <div ref={ref}>
        <Group
          position="apart"
          noWrap
          onClick={(e) => {
            // e.preventDefault(); // Prevent select from closing
            // e.stopPropagation();
            onAddClick({ label, value });
          }}
          style={{ cursor: "pointer", marginBottom: 5 }}
        >
          <Text>{label}</Text>
          <Button
            size="xs"
            disabled={added}
            variant="transparent"
            color="green"
            c={"green"}
          >
            {added ? "Added" : "Add"}
          </Button>
        </Group>
      </div>
    )
  );
  const handleRemoveProvider = (value) => {
    setAddedProviders((prev) => prev?.filter((f) => f?.value !== value));
  };

  const handleAddProvider = (provider) => {
    if (!addedProviders?.some((f) => f?.value === provider?.value)) {
      setAddedProviders((prev) => [...prev, provider]);
      if (selectRef.current) {
        selectRef.current.blur();
      }
    }
  };

  return (
    <Box mt={20}>
      <Grid>
        <Grid.Col span={isMobile ? 12 : 12}>
          {(mode === "add" || mode === "edit") && (
            <>
              <Text className={classes.clientTitle} mt={20}>
                Select Provider
              </Text>

              <Select
                ref={selectRef}
                size="md"
                radius={"md"}
                placeholder="Select a provider"
                itemComponent={(props) => (
                  <ProviderItem
                    {...props}
                    onAddClick={handleAddProvider}
                    added={addedProviders?.some(
                      (f) => f?.value === props?.value
                    )}
                  />
                )}
                data={
                  skypointProviders
                    ?.map((prov) => ({
                      value: prov?.id,
                      label: prov?.name,
                    })) || []
                }
                searchable
                nothingFound="No provider found"
                maxDropdownHeight={200}
                withinPortal
                rightSectionWidth={0}
                {...form.getInputProps("providerId")}
                rightSectionPointerEvents="none"
                rightSection={icon}
                disabled={mode === "view"}
              />
            </>
          )}

          <Group mt={10} gap="xs" wrap="wrap">
            {mode === "view" && (
              <>
                <Box mt={10}>
                  <Text mb={5}>Providers</Text>
                  <Group gap="xs" wrap="wrap" style={{ marginTop: ".5rem" }}>
                    {data?.providerId?.map((f) => (
                      <Badge
                        key={f.id}
                        variant="light"
                        color="#F2F4F7"
                        radius="lg"
                        size="lg"
                        c={"#344054"}
                        style={{ background: "#F2F4F7" }}
                      >
                        {f?.name}
                      </Badge>
                    ))}
                  </Group>
                </Box>
              </>
            )}
            {(mode === "edit" || mode === "add") && (
              <>
                {addedProviders?.map((f) => (
                  <Badge
                    key={f?.value}
                    variant="light"
                    color="gray"
                    radius="lg"
                    size="lg"
                    c={"#344054"}
                    rightSection={
                      <CloseButton
                        size="xs"
                        c="#344054"
                        onClick={() => handleRemoveProvider(f?.value)}
                      />
                    }
                  >
                    {f?.label}
                  </Badge>
                ))}
              </>
            )}
          </Group>
        </Grid.Col>
      </Grid>
      {(mode === "add" || mode === "edit") && (
        <>
          <Divider my={10} />
          <Box pt={20}>
            <SkypointActionButtons
              onCancel={handleCancel}
              type="submit"
              activeTab={activeTab}
              onSave={handleSave}
              loading={addStaffLoading}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
